<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import type { LineBlockLine } from "@types"
import { useResponsive } from "@core/composables"
import { formatDate, formatNumber } from "@core/helpers"
import { isOptimTurpeTemplateC2 } from "@modules/courtage/constants/optim-turpe"
import type { OptimTurpeResultDetailProps } from "@modules/courtage/types/optim-turpe"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            titles: {
                heading1: "Détails de l'analyse",
            },
            subtitles: {
                totalEnergie: "Consommation sur la période : {value} kWh",
            },
            fields: {
                connectionPower: "Puissance de raccordement",
                connection: "Branchement",
                period: "Période d’analyse",
                hcSingle: "Plage Heure Creuse (HC)",
                hcPlural: "Plages Heures Creuses (HC)",
            },
            values: {
                connectionMono: "Monophasé",
                connectionTri: "Triphasé",
            },
            plages: {
                hpb: "HPB",
                hcb: "HCB",
                hph: "HPH",
                hch: "HCH",
                pte: "PTE",
            },
            texts: {
                popup_branchement_tri: "Puissance souscrite supérieure à 9 kVA",
                popup_branchement_mono:
                    "Puissance souscrite inférieure à 18 kVA",
                no_data:
                    "Impossible de calculer les plages temporelles car pas de données infrahoraires",
                no_data_help:
                    "Les répartitions par plage horaire nécessitent un historique infrahoraire.",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type TimelineBarRow = {
    plageKey: string
    valueFormatted: string
    width: string
    borderColor: string
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<OptimTurpeResultDetailProps>(), {
    timelinePlages: () => [],
    totalConsumptionKwh: undefined,
    connectionTriphase: undefined,
    periodDateFrom: undefined,
    periodDateTo: undefined,
    heuresCreusesDisplay: undefined,
    heuresCreusesPeriodCount: undefined,
})

// ================================
// COMPOSABLES
// ================================
const { isMobile } = useResponsive()

// ================================
// STATE
// ================================
/** Valeur du panneau accordéon (ouvert par défaut). */
const ACCORDION_PANEL_VALUE = "detail"
const accordionValue = ref<string | undefined>(ACCORDION_PANEL_VALUE)

/**
 * Couleurs des barres timeline par clé de plage (aligné legacy OptimCardResultDetail).
 */
const COLORS_CLASSES: Record<string, string> = {
    hcb: "#F29222",
    hpb: "#936FAC",
    hch: "#FAC723",
    hph: "#0CB2AF",
    pte: "#A1C65D",
}

/** Ordre d’affichage des plages sur la timeline (hors PTE ajoutée si template C2). */
const PLAGE_ORDER = ["hcb", "hpb", "hch", "hph"] as const

/** Marge minimale en px pour le calcul de largeur relative des barres. */
const MIN_WIDTH_PX = 60

// ================================
// COMPUTED
// ================================
/** Indique un segment C5 (affichage champs branchement / HC). */
const getIsStrictC5 = computed(() => props.segment === "C5")

/** Affiche la barre de répartition si la conso totale est connue. */
const getShowTimeline = computed(
    () =>
        props.totalConsumptionKwh != null &&
        !Number.isNaN(props.totalConsumptionKwh)
)

/** Conso totale formatée pour le sous-titre de la timeline. */
const getTotalConsumptionFormatted = computed(() =>
    formatNumber(props.totalConsumptionKwh ?? 0, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    })
)

/** Sous-titre sous la timeline (conso période en kWh). */
const getTimelineSubtitle = computed(() =>
    t("subtitles.totalEnergie", {
        value: getTotalConsumptionFormatted.value,
    })
)

/** kWh arrondis par clé de plage (entrée timeline). */
const getKwhByPlage = computed(() => {
    const m: Record<string, number> = {}
    for (const row of props.timelinePlages) {
        m[row.plageKey] = row.kwh
    }
    return m
})

/** Parts d’énergie par plage (0 si absent) pour totaux et largeurs. */
const getEnergyParts = computed(() => {
    const m = getKwhByPlage.value
    return {
        hpb: m.hpb ?? 0,
        hcb: m.hcb ?? 0,
        hph: m.hph ?? 0,
        hch: m.hch ?? 0,
        pte: m.pte ?? 0,
    }
})

/** Template tarifaire C2 / P1 (PTE affichée). */
const getIsTemplateC2 = computed(() => isOptimTurpeTemplateC2(props.segment))

/** Bandeau « pas de données » si la somme des parts est quasi nulle. */
const getShowNoDataBanner = computed(() => {
    const { hpb, hcb, hph, hch, pte } = getEnergyParts.value
    const sumFour = hpb + hcb + hph + hch + (getIsTemplateC2.value ? pte : 0)
    return sumFour <= 20
})

/** Clés de plages dans l’ordre d’affichage (avec PTE si C2). */
const getOrderedPlageKeys = computed((): string[] => {
    const keys: string[] = [...PLAGE_ORDER]
    if (getIsTemplateC2.value) keys.push("pte")
    return keys
})

/** Lignes de la timeline (largeur, couleur, valeur kWh formatée). */
const getTimelineBarRows = computed((): TimelineBarRow[] => {
    const { hpb, hcb, hph, hch, pte } = getEnergyParts.value
    const isC2 = getIsTemplateC2.value
    const byPlage = getKwhByPlage.value

    return getOrderedPlageKeys.value.map((plageKey) => {
        const kwh = byPlage[plageKey] ?? 0
        const width = computePlageWidth(plageKey, {
            hpb,
            hcb,
            hph,
            hch,
            pte,
            isC2,
        })
        const valueFormatted =
            kwh === 0
                ? "-"
                : `${formatNumber(kwh, {
                      maximumFractionDigits: 0,
                      minimumFractionDigits: 0,
                  })} kWh`
        return {
            plageKey,
            valueFormatted,
            width,
            borderColor: COLORS_CLASSES[plageKey] ?? "#64748b",
        }
    })
})

/** Période d’analyse formatée pour les champs détail. */
const getPeriodDisplay = computed((): string => {
    const from = props.periodDateFrom
    const to = props.periodDateTo
    if (!from || !to) return "-"
    return `Du ${formatDate(from, "DD/MM/YYYY")} au ${formatDate(to, "DD/MM/YYYY")}`
})

/** Libellé champ HC singulier / pluriel selon le nombre de périodes. */
const getHcFieldLabel = computed(() => {
    const n = props.heuresCreusesPeriodCount ?? 0
    return n > 1 ? t("fields.hcPlural") : t("fields.hcSingle")
})

/** Lignes du bloc récap (puissance, branchement, période, HC selon segment). */
const getLineBlocks = computed((): LineBlockLine[] => {
    const lines: LineBlockLine[] = [
        {
            id: "connectionPower",
            text: t("fields.connectionPower"),
            value: props.connectionPowerDisplay,
        },
    ]

    if (getIsStrictC5.value) {
        const tri = props.connectionTriphase
        if (tri !== undefined) {
            lines.push({
                id: "connection",
                text: t("fields.connection"),
                value: tri
                    ? t("values.connectionTri")
                    : t("values.connectionMono"),
                tooltip: resolveBranchementHelpText(tri),
            })
        }
        lines.push({
            id: "period",
            text: t("fields.period"),
            value: getPeriodDisplay.value,
        })
        lines.push({
            id: "hc",
            text: getHcFieldLabel.value,
            value: props.heuresCreusesDisplay ?? "-",
        })
    } else {
        lines.push({
            id: "period",
            text: t("fields.period"),
            value: getPeriodDisplay.value,
        })
    }

    return lines
})

// ================================
// BUSINESS LOGIC
// ================================
/**
 * Texte d’aide contextuel monophasé / triphasé (branchement C5).
 * @param isTriphase - Indique un raccordement triphasé.
 * @returns Libellé explicatif pour tooltip.
 */
function resolveBranchementHelpText(isTriphase: boolean): string {
    return isTriphase
        ? t("texts.popup_branchement_tri")
        : t("texts.popup_branchement_mono")
}

/**
 * Libellé i18n d’une plage horaire, avec repli sur la clé en majuscules.
 * @param plageKey - Clé technique (hpb, hcb, …).
 * @returns Libellé affiché.
 */
function resolvePlageLabel(plageKey: string): string {
    const key = `plages.${plageKey}`
    const translated = t(key)
    return translated !== key ? translated : plageKey.toUpperCase()
}

/**
 * Largeur relative d’une barre de plage (legacy + marge min, PTE si C2/P1).
 * @param type - Clé de plage.
 * @param parts - Parts kWh et indicateur C2 pour le total.
 * @returns Largeur CSS (pourcentage).
 */
function computePlageWidth(
    type: string,
    parts: {
        hpb: number
        hcb: number
        hph: number
        hch: number
        pte: number
        isC2: boolean
    }
): string {
    const { hpb, hcb, hph, hch, pte, isC2 } = parts
    const total = hpb + hcb + hph + hch + MIN_WIDTH_PX + (isC2 ? pte : 0)
    const pct = (n: number) => (n === 0 ? "25%" : `${(n / total) * 100 + 15}%`)

    switch (type) {
        case "hpb":
            return pct(hpb)
        case "hcb":
            return pct(hcb)
        case "hph":
            return pct(hph)
        case "hch":
            return pct(hch)
        case "pte":
            return pct(pte)
        default:
            return "25%"
    }
}

/**
 * Styles inline d’une barre de timeline (largeur responsive + couleur de bordure).
 * @param row - Ligne timeline (largeur relative + couleur).
 * @returns Propriétés de style pour l’élément barre.
 */
function buildTimelineItemStyle(row: TimelineBarRow): Record<string, string> {
    return {
        width: isMobile.value ? "100%" : row.width,
        borderTopColor: row.borderColor,
    }
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL — détail analyse (timeline + champs) -->
    <!-- ================================ -->
    <BaseAccordion
        class="optim-turpe-result-detail"
        v-model:value="accordionValue"
    >
        <BaseAccordionPanel :value="ACCORDION_PANEL_VALUE">
            <BaseAccordionHeader>
                {{ t("titles.heading1") }}
            </BaseAccordionHeader>
            <BaseAccordionContent>
                <!-- ================================ -->
                <!-- CONTENU PRINCIPAL -->
                <!-- ================================ -->
                <div class="mt-1 flex flex-col gap-6">
                    <div
                        v-if="getShowTimeline"
                        class="flex flex-col overflow-hidden rounded-md border border-surface-200"
                    >
                        <p
                            class="bg-primary-50 px-4 py-4 text-center text-sm font-medium text-primary-950"
                        >
                            {{ getTimelineSubtitle }}
                        </p>
                        <div
                            class="flex flex-col gap-1 border-t border-primary-100 md:flex-row md:gap-1"
                        >
                            <div
                                v-for="item in getTimelineBarRows"
                                :key="item.plageKey"
                                class="min-w-0 w-full shrink-0 border-t-4 px-2 py-3 max-md:!w-full md:min-w-[4rem] md:flex-1 md:px-3"
                                :style="buildTimelineItemStyle(item)"
                            >
                                <p class="text-xs font-medium text-primary-700">
                                    {{ resolvePlageLabel(item.plageKey) }}
                                </p>
                                <p
                                    class="text-sm font-semibold text-primary-950"
                                >
                                    {{ item.valueFormatted }}
                                </p>
                            </div>
                        </div>
                        <p
                            v-if="getShowNoDataBanner"
                            class="flex flex-wrap items-center justify-center gap-2 border-t border-surface-200 bg-primary-50 px-4 py-3 text-center text-sm text-primary-800"
                        >
                            {{ t("texts.no_data") }}
                            <BaseIcon
                                v-tooltip.top="t('texts.no_data_help')"
                                icon="pi pi-info-circle"
                                class="cursor-help text-primary-600"
                            />
                        </p>
                    </div>

                    <div class="flex flex-col px-1">
                        <BaseLineBlock
                            v-for="line in getLineBlocks"
                            :key="line.id ?? line.text"
                            :line="line"
                            :loading="false"
                        />
                    </div>
                </div>
            </BaseAccordionContent>
        </BaseAccordionPanel>
    </BaseAccordion>
</template>
